import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, ShieldCheck, User } from 'lucide-react';
import { Player, UserProfile } from '../../types';
import { collection, query, orderBy, onSnapshot, addDoc, doc, updateDoc, getDocs, where, limitToLast, setDoc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { motion, AnimatePresence } from 'motion/react';
import { FadeInImage } from './FadeInImage';

interface Props {
  player: Player;
  currentUser: UserProfile;
  onClose: () => void;
  isAdminModeActive?: boolean;
}

interface SupportMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: number;
  isOptimistic?: boolean;
}

const formatMessageDateTime = (timestamp: number) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;
  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  if (msgDate === today) {
    return `Hoy, ${timeStr}`;
  } else if (msgDate === yesterday) {
    return `Ayer, ${timeStr}`;
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year} - ${timeStr}`;
  }
};

export function SupportChatModal({ player, currentUser, onClose, isAdminModeActive = false }: Props) {
  const isAdmin = currentUser.role === 'admin' || (currentUser.role === 'superadmin' && isAdminModeActive);
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [optimisticMessages, setOptimisticMessages] = useState<SupportMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const allMessages = [
    ...messages,
    ...optimisticMessages.filter(optMsg => 
      !messages.some(m => 
        m.senderId === optMsg.senderId && 
        m.text === optMsg.text && 
        Math.abs(m.createdAt - optMsg.createdAt) < 10000
      )
    )
  ].sort((a, b) => a.createdAt - b.createdAt);

  const [limitCount, setLimitCount] = useState(15);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialRef = useRef(true);
  const isPaginatingRef = useRef(false);
  const scrollHeightRef = useRef(0);
  const prevFirstIdRef = useRef<string | undefined>(undefined);
  const prevMessagesLengthRef = useRef(0);

  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      const vv = window.visualViewport;
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: `${vv.height}px`,
        zIndex: 200,
      };
    }
    return {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 200,
    };
  });

  const [cardStyle, setCardStyle] = useState<React.CSSProperties>(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      const scale = window.visualViewport.scale || 1;
      return {
        transform: `scale(${1 / scale})`,
        transformOrigin: 'center center',
      };
    }
    return {};
  });

  useEffect(() => {
    // Record current scroll position to freeze background scroll beautifully
    const scrollY = window.scrollY;

    // Save original styles of document.body
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalWidth = document.body.style.width;

    // Freeze background scroll completely (necessary for iOS Safari)
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.width = '100%';

    // Track visual viewport only for virtual keyboard height adjustments
    const updateViewport = () => {
      if (window.visualViewport) {
        const vv = window.visualViewport;
        setViewportStyle({
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: `${vv.height}px`,
          zIndex: 200,
        });
        const scale = vv.scale || 1;
        setCardStyle({
          transform: `scale(${1 / scale})`,
          transformOrigin: 'center center',
        });
      }
    };

    updateViewport();

    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);

    // Globally prevent native pinch-to-zoom gestures in capture phase to completely lock zoom while modal is open
    const preventZoomCapture = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };

    // Stop native touch event propagation for single-finger touches to prevent triggering global index.html virtual zoom
    const handleTouchStartMove = (e: TouchEvent) => {
      e.stopPropagation();
    };

    const handleTouchEndCancel = (e: TouchEvent) => {
      e.stopPropagation();
    };

    // Register capture-phase listeners on window to guarantee blocking native zoom before any stopPropagation
    window.addEventListener('touchstart', preventZoomCapture, { capture: true, passive: false });
    window.addEventListener('touchmove', preventZoomCapture, { capture: true, passive: false });

    const el = modalRef.current;
    if (el) {
      el.addEventListener('touchstart', handleTouchStartMove, { passive: true });
      el.addEventListener('touchmove', handleTouchStartMove, { passive: true });
      el.addEventListener('touchend', handleTouchEndCancel, { passive: true });
      el.addEventListener('touchcancel', handleTouchEndCancel, { passive: true });
    }

    return () => {
      // Restore original body styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;

      // Restore physical scroll position
      window.scrollTo(0, scrollY);

      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('touchstart', preventZoomCapture, { capture: true });
      window.removeEventListener('touchmove', preventZoomCapture, { capture: true });
      if (el) {
        el.removeEventListener('touchstart', handleTouchStartMove);
        el.removeEventListener('touchmove', handleTouchStartMove);
        el.removeEventListener('touchend', handleTouchEndCancel);
        el.removeEventListener('touchcancel', handleTouchEndCancel);
      }
    };
  }, [mounted]);

  // Reset when player.id changes
  useEffect(() => {
    isInitialRef.current = true;
    setLimitCount(15);
    setHasMore(true);
    setOptimisticMessages([]);
    prevFirstIdRef.current = undefined;
    prevMessagesLengthRef.current = 0;
  }, [player.id]);

  useEffect(() => {
    // We use a consistent ID for the support chat room
    // For admin-user chat, we can use the player's ID as the document ID in 'supportChats'
    const q = query(
      collection(db, getCollectionPath('support_chats'), player.id, 'messages'),
      orderBy('createdAt', 'asc'),
      limitToLast(limitCount)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: SupportMessage[] = [];
      snapshot.forEach(doc => {
        msgs.push({ id: doc.id, ...doc.data() } as SupportMessage);
      });

      if (msgs.length < limitCount) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [player.id, limitCount]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (container.scrollTop <= 5 && hasMore && !isPaginatingRef.current) {
      scrollHeightRef.current = container.scrollHeight;
      isPaginatingRef.current = true;
      setLimitCount(prev => prev + 15);
    }
  };

  useEffect(() => {
    const currentFirstId = allMessages[0]?.id;
    const prevFirstId = prevFirstIdRef.current;

    if (isInitialRef.current && allMessages.length > 0) {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
      isInitialRef.current = false;
    } else if (isPaginatingRef.current && containerRef.current) {
      const container = containerRef.current;
      const addedHeight = container.scrollHeight - scrollHeightRef.current;
      container.scrollTop = addedHeight;
      isPaginatingRef.current = false;
    } else if (prevFirstId && currentFirstId === prevFirstId && allMessages.length > prevMessagesLengthRef.current) {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }

    prevFirstIdRef.current = currentFirstId;
    prevMessagesLengthRef.current = allMessages.length;
  }, [allMessages]);

  useEffect(() => {
    // Mark as read and guarantee the parent document exists
    const chatRef = doc(db, getCollectionPath('support_chats'), player.id);
    
    setDoc(chatRef, {
      [isAdmin ? 'unreadByAdmin' : 'unreadByUser']: false,
      userId: player.id,
      userName: player.name
    }, { merge: true }).catch((err) => {
      console.error("Error setting/updating support chat document:", err);
    });
  }, [player.id, isAdmin, player.name]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    const optimisticId = `opt-${Date.now()}`;
    const optimisticMsg: SupportMessage = {
      id: optimisticId,
      senderId: currentUser.uid,
      text: messageText,
      createdAt: Date.now(),
      isOptimistic: true
    };

    setOptimisticMessages(prev => [...prev, optimisticMsg]);

    // Run in background asynchronously so it's non-blocking to the user
    (async () => {
      try {
        await addDoc(collection(db, getCollectionPath('support_chats'), player.id, 'messages'), {
          senderId: currentUser.uid,
          text: messageText,
          createdAt: optimisticMsg.createdAt
        });

        // Update/create the main chat doc with last message info
        await setDoc(doc(db, getCollectionPath('support_chats'), player.id), {
          lastMessage: messageText,
          lastMessageAt: Date.now(),
          userId: player.id,
          userName: player.name,
          unreadByAdmin: !isAdmin,
          unreadByUser: isAdmin
        }, { merge: true });

        // Send notification to the other party
        // If the player is the sender, notify all superadmins
        // If an admin is the sender, notify the player
        if (!isAdmin) {
          // Do not write to notifications for superadmins anymore!
          // Superadmins will receive the message directly in their private chats/messages dropdown mapped from support_chats collection.
        } else {
          await addDoc(collection(db, getCollectionPath('notifications')), {
            userId: player.id,
            type: 'support_chat_message',
            title: 'Mensaje de Soporte',
            body: messageText,
            playerId: player.id,
            read: false,
            createdAt: Date.now()
          });
        }

        // Remove from optimistic list
        setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticId));
      } catch (error) {
        console.error("Error sending support message:", error);
        setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticId));
        // Restore input so the user doesn't lose what they wrote
        setNewMessage(messageText);
      }
    })();
  };

  if (!mounted) return null;

  return createPortal(
    <div ref={modalRef} className="z-[200] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 md:p-6 animate-in fade-in" style={viewportStyle} onClick={onClose}>
      <div style={cardStyle} className="w-full max-w-lg h-[600px] max-h-[90%] flex flex-col pointer-events-auto">
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-full h-full theme-bg-card border border-white/10 rounded-[2rem] overflow-hidden flex flex-col shadow-2xl text-white" 
          onClick={e => e.stopPropagation()}
        >
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-theme-primary/10 flex items-center justify-center border border-theme-primary/20">
              {isAdmin ? (
                player.photoUrl ? (
                  <FadeInImage src={player.photoUrl} alt={player.name} className="w-full h-full object-cover rounded-2xl" />
                ) : (
                  <User className="theme-primary-text" size={24} />
                )
              ) : (
                <ShieldCheck className="theme-primary-text" size={24} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tight">
                {isAdmin ? player.name : 'Soporte Oficial RPT'}
              </h2>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-[10px] font-black uppercase tracking-widest text-white/40">
                  {isAdmin ? 'Consulta de Jugador' : 'Canal de Ayuda'}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Messages */}
        <div 
          ref={containerRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4 bg-black/20"
        >
          {allMessages.length > 0 && (
            hasMore ? (
              <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/30 font-bold animate-pulse">
                ↾ Desliza hacia arriba para ver mensajes anteriores ↾
              </div>
            ) : (
              <div className="text-center py-2 text-[10px] uppercase tracking-widest text-white/20 font-bold">
                ✨ Inicio de la conversación — No hay más mensajes anteriores
              </div>
            )
          )}
          {allMessages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center opacity-30 text-center gap-4 py-12">
              <div className="p-4 bg-white/5 rounded-full">
                <Send size={32} />
              </div>
              <p className="text-sm font-bold uppercase tracking-widest">
                No hay mensajes aún.<br/>Inicia la conversación.
              </p>
            </div>
          )}
          {allMessages.map((msg) => {
            const isMe = msg.senderId === currentUser.uid;
            return (
              <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-200`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm font-medium ${
                  isMe 
                    ? 'theme-primary-bg text-black rounded-tr-sm shadow-lg' 
                    : 'bg-white/10 text-white rounded-tl-sm border border-white/5'
                } ${msg.isOptimistic ? 'opacity-60 animate-pulse' : 'opacity-100 transition-opacity'}`}>
                  {msg.text}
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest opacity-30 mt-1.5 px-1">
                  {formatMessageDateTime(msg.createdAt)}
                </span>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-6 bg-white/[0.02] border-t border-white/5">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <input
              autoFocus
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              className="flex-1 bg-black/40 border border-white/10 focus:theme-primary-border rounded-2xl px-6 py-4 text-sm font-bold outline-none transition-all placeholder:opacity-30"
            />
            <button 
              type="submit" 
              disabled={!newMessage.trim()} 
              className="w-14 h-14 flex items-center justify-center rounded-2xl theme-primary-bg text-black disabled:opacity-50 hover:scale-105 active:scale-95 transition-all shadow-xl shrink-0"
            >
              <Send size={20} className="translate-x-0.5" />
            </button>
          </form>
        </div>
        </motion.div>
      </div>
    </div>,
    document.body
  );
}
